import type { ReactNode } from "react";
import { cx } from "./cx";

/** Where the bubble sits relative to its trigger. `left`/`right` suit vertical
 *  control stacks (e.g. corner map controls) where above/below would clip at an
 *  edge or cover the neighbouring button. */
export type TooltipPosition = "above" | "below" | "left" | "right";

export interface TooltipProps {
  /** Tooltip body — appears INSTANTLY on hover/focus (motion.duration.fast fade). */
  content: ReactNode;
  children: ReactNode;
  position?: TooltipPosition;
  /** Force-show (gallery state display / programmatic use). */
  open?: boolean;
  className?: string;
}

const POSITION_CLASS: Record<TooltipPosition, string | false> = {
  above: false,
  below: "pz-tooltip--below",
  left: "pz-tooltip--left",
  right: "pz-tooltip--right",
};

/**
 * The custom instant hover tooltip — the ONLY sanctioned hover-explanation
 * affordance. Native `title=` attributes and the help cursor are banned
 * product-wide (DESIGN_SYSTEM.md §9; enforced by token-adherence.mjs).
 * CSS-driven (:hover/:focus-within), so it needs no client JS.
 */
export function Tooltip({ content, children, position = "above", open = false, className }: TooltipProps) {
  return (
    <span className={cx("pz-tooltip-wrap", className)}>
      {children}
      <span role="tooltip" className={cx("pz-tooltip", POSITION_CLASS[position], open && "pz-tooltip--open")}>
        {content}
      </span>
    </span>
  );
}
